import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import { WorkoutProvider } from './context/WorkoutContext';
import SavedWorkouts from './components/SavedWorkouts';
import ProgressGraphs from './components/ProgressGraphs';
import CardGallery from './pages/CardGallery';
import CardBattle from './pages/CardBattle';
import CardPage from './components/CardPage';
import MoveCardDisplay from './components/MoveCardDisplay';


// Import page components - Your working structure
import Home from './pages/Home';
import ExerciseSelector from './pages/ExerciseSelector';
import RepCountScreen from './pages/RepCountScreen';
import WorkoutSummary from './pages/WorkoutSummary';
import Workout from './pages/Workout';
import WorkoutComplete from './pages/WorkoutComplete';
import CustomBattleSetup from './pages/CustomBattleSetup';

// NEW CATEGORY PAGES
import TrainPage from './pages/TrainPage';
import BuildPage from './pages/BuildPage';
import BattlePage from './pages/BattlePage';

import WorkoutMilestones from './pages/WorkoutMilestones';
import ManualWorkout from './pages/ManualWorkout';

import BattleStoryMode from './components/BattleStoryMode';


// New Selection Components
import BodyweightSelection from './pages/BodyweightSelection';
import BandsSelection from './pages/BandsSelection';
import StrengthSelection from './pages/StrengthSelection';
import AdvancedBodyweightSelection from './pages/AdvancedBodyweightSelection';
import EverythingSelection from './pages/EverythingSelection';
import BattleSelection from './pages/BattleSelection';
import HeroSelection from './pages/HeroSelection';

// Battle Mode Components
import BattleWorkout from './pages/BattleWorkout';
import BattleComplete from './pages/BattleComplete';
import BattleRepCount from './pages/BattleRepCount';

// Algorithm Train Component
import AlgorithmTrain from './pages/AlgorithmTrain';

// Train Mode Components
import TrainCategories from './pages/TrainCategories';
import BodyweightTrainers from './pages/BodyweightTrainers';
import StrengthTrainers from './pages/StrengthTrainers';
import BandsTrainers from './pages/BandsTrainers';
import EverythingTrainers from './pages/EverythingTrainers';
import HeroTrainers from './pages/HeroTrainers';
import TrainerPage from './pages/TrainerPage';
import AdvancedTrainerPage from './pages/AdvancedTrainerPage';
import AdvancedBodyweightTrainers from './pages/AdvancedBodyweightTrainers';

// Japan Train Mode Components
import JapanTrainCategories from './pages/JapanTrainCategories';
import JapanOneTrainers from './pages/JapanOneTrainers';
import JapanTwoTrainers from './pages/JapanTwoTrainers';
import JapanThreeTrainers from './pages/JapanThreeTrainers';
import JapanFourTrainers from './pages/JapanFourTrainers';
import JapanFiveTrainers from './pages/JapanFiveTrainers';
import JapanTrainerPage from './pages/JapanTrainerPage';

// Knights Train Mode Components
import KnightsTrainCategories from './pages/KnightsTrainCategories';
import KnightsOneTrainers from './pages/KnightsOneTrainers';
import KnightsTwoTrainers from './pages/KnightsTwoTrainers';
import KnightsThreeTrainers from './pages/KnightsThreeTrainers';
import KnightsFourTrainers from './pages/KnightsFourTrainers';
import KnightsFiveTrainers from './pages/KnightsFiveTrainers';
import KnightsTrainerPage from './pages/KnightsTrainerPage';

// History Train Mode Components
import HistoryTrainCategories from './pages/HistoryTrainCategories';
import HistoryOneTrainers from './pages/HistoryOneTrainers';
import HistoryTwoTrainers from './pages/HistoryTwoTrainers';
import HistoryThreeTrainers from './pages/HistoryThreeTrainers';
import HistoryFourTrainers from './pages/HistoryFourTrainers';
import HistoryFiveTrainers from './pages/HistoryFiveTrainers';
import HistoryTrainerPage from './pages/HistoryTrainerPage';

// Native American Train Mode Components
import NativeAmericanTrainCategories from './pages/NativeAmericanTrainCategories';
import NativeAmericanOneTrainers from './pages/NativeAmericanOneTrainers';
import NativeAmericanTwoTrainers from './pages/NativeAmericanTwoTrainers';
import NativeAmericanThreeTrainers from './pages/NativeAmericanThreeTrainers';
import NativeAmericanFourTrainers from './pages/NativeAmericanFourTrainers';
import NativeAmericanFiveTrainers from './pages/NativeAmericanFiveTrainers';
import NativeAmericanTrainerPage from './pages/NativeAmericanTrainerPage';



// Import Sound Manager
import { soundManager } from './utils/SoundManager';


function App() {
    // Initialize sounds when app loads
    useEffect(() => {
        console.log('🔊 Initializing sound system...');
        soundManager.initializeSounds();
        console.log('🎵 Sound system ready!');
    }, []);

    return (
        <WorkoutProvider>
            <Router>
                <div className="App">
                    <Routes>
                        {/* NEW HOME AND CATEGORY ROUTES */}
                        <Route path="/" element={<Home/>}/>
                        <Route path="/train-page" element={<TrainPage />} />
                        <Route path="/build-page" element={<BuildPage />} />
                        <Route path="/battle-page" element={<BattlePage />} />

                        
                        {/* NEW MILESTONE AND MANUAL WORKOUT ROUTES */}
                        <Route path="/workout-milestones" element={<WorkoutMilestones />} />
                        <Route path="/manual-workout" element={<ManualWorkout />} />

                        {/* ALGORITHM TRAIN */}
                        <Route path="/algorithm-train" element={<AlgorithmTrain />} />

                        {/* CUSTOM BATTLE SETUP ROUTE */}
                        <Route path="/custom-battle-setup" element={<CustomBattleSetup />} />

                        <Route path="/battle-story-mode" element={<BattleStoryMode />} />

                        <Route path="/card-gallery" element={<CardGallery />} />
                        <Route path="/card-battle" element={<CardBattle />} />
                        <Route path="/card-battle/:battleId" element={<CardBattle />} />
                        <Route path="/cards/:cardNumber" element={<CardGallery />} />

                        {/* CORE WORKOUT FLOW ROUTES */}
                        <Route path="/exercise-selector" element={<ExerciseSelector />} />
                        <Route path="/rep-count" element={<RepCountScreen />} />
                        <Route path="/workout-summary" element={<WorkoutSummary />} />
                        <Route path="/workout" element={<Workout />} />
                        <Route path="/workout-complete" element={<WorkoutComplete />} />
                        <Route path="/saved-workouts" element={<SavedWorkouts />} />
                        <Route path="/progress-graphs" element={<ProgressGraphs />} />

                        {/* BUILD CATEGORY SELECTION ROUTES */}
                        <Route path="/bodyweight-selection" element={<BodyweightSelection />} />
                        <Route path="/bands-selection" element={<BandsSelection />} />
                        <Route path="/strength-selection" element={<StrengthSelection />} />
                        <Route path="/advanced-bodyweight-selection" element={<AdvancedBodyweightSelection />} />
                        <Route path="/everything-selection" element={<EverythingSelection />} />
                        <Route path="/hero-selection" element={<HeroSelection />} />

                        <Route path="/battle-cards/:cardNumber" element={<CardPage />} />


                        {/* BATTLE CATEGORY SELECTION ROUTES */}
                        <Route path="/battle-selection" element={<BattleSelection />} />

                        {/* JAPAN TRAIN MODE ROUTES */}
                        <Route path="/japan-train-categories" element={<JapanTrainCategories />} />
                        <Route path="/japan-one-trainers" element={<JapanOneTrainers />} />
                        <Route path="/japan-two-trainers" element={<JapanTwoTrainers />} />
                        <Route path="/japan-three-trainers" element={<JapanThreeTrainers />} />
                        <Route path="/japan-four-trainers" element={<JapanFourTrainers />} />
                        <Route path="/japan-five-trainers" element={<JapanFiveTrainers />} />

                        {/* KNIGHTS TRAIN MODE ROUTES */}
                        <Route path="/knights-train-categories" element={<KnightsTrainCategories />} />
                        <Route path="/knights-one-trainers" element={<KnightsOneTrainers />} />
                        <Route path="/knights-two-trainers" element={<KnightsTwoTrainers />} />
                        <Route path="/knights-three-trainers" element={<KnightsThreeTrainers />} />
                        <Route path="/knights-four-trainers" element={<KnightsFourTrainers />} />
                        <Route path="/knights-five-trainers" element={<KnightsFiveTrainers />} />

                        {/* HISTORY TRAIN MODE ROUTES */}
                        <Route path="/history-train-categories" element={<HistoryTrainCategories />} />
                        <Route path="/history-one-trainers" element={<HistoryOneTrainers />} />
                        <Route path="/history-two-trainers" element={<HistoryTwoTrainers />} />
                        <Route path="/history-three-trainers" element={<HistoryThreeTrainers />} />
                        <Route path="/history-four-trainers" element={<HistoryFourTrainers />} />
                        <Route path="/history-five-trainers" element={<HistoryFiveTrainers />} />

                        {/* NATIVE AMERICAN TRAIN MODE ROUTES */}
                        <Route path="/native-american-train-categories" element={<NativeAmericanTrainCategories />} />
                        <Route path="/native-american-one-trainers" element={<NativeAmericanOneTrainers />} />
                        <Route path="/native-american-two-trainers" element={<NativeAmericanTwoTrainers />} />
                        <Route path="/native-american-three-trainers" element={<NativeAmericanThreeTrainers />} />
                        <Route path="/native-american-four-trainers" element={<NativeAmericanFourTrainers />} />
                        <Route path="/native-american-five-trainers" element={<NativeAmericanFiveTrainers />} />

                        {/* TRAIN MODE ROUTES */}
                        <Route path="/train-categories" element={<TrainCategories />} />
                        <Route path="/bodyweight-trainers" element={<BodyweightTrainers />} />
                        <Route path="/advanced-bodyweight-trainers" element={<AdvancedBodyweightTrainers />} />
                        <Route path="/strength-trainers" element={<StrengthTrainers />} />
                        <Route path="/bands-trainers" element={<BandsTrainers />} />
                        <Route path="/everything-trainers" element={<EverythingTrainers />} />
                        <Route path="/hero-trainers" element={<HeroTrainers />} />

                        {/* REGULAR TRAINER ROUTES */}
                        <Route path="/trainer/:trainerId" element={<TrainerPage />} />
                        <Route path="/trainer/:trainerId/:programId/intensity-selection" element={<TrainerPage />} />

                        {/* ADVANCED TRAINER ROUTES */}
                        <Route path="/advanced-trainer/:trainerId" element={<AdvancedTrainerPage />} />

                        {/* STRENGTH TRAINER ROUTES */}
                        <Route path="/strength-trainer/:trainerId" element={<TrainerPage />} />

                        {/* BANDS TRAINER ROUTES */}
                        <Route path="/bands-trainer/:trainerId" element={<TrainerPage />} />

                        {/* EVERYTHING TRAINER ROUTES */}
                        <Route path="/everything-trainer/:trainerId" element={<TrainerPage />} />

                        {/* HERO TRAINER ROUTES */}
                        <Route path="/hero-trainers/:trainerId" element={<HeroTrainers />} />

                        {/* JAPAN TRAINER ROUTES */}
                        <Route path="/japan-trainer/:trainerId" element={<JapanTrainerPage />} />
                        <Route path="/japan-trainer/:trainerId/:programId/intensity-selection" element={<JapanTrainerPage />} />

                        {/* KNIGHTS TRAINER ROUTES */}
                        <Route path="/knight-trainer/:trainerId" element={<KnightsTrainerPage />} />
                        <Route path="/knight-trainer/:trainerId/:programId/intensity-selection" element={<KnightsTrainerPage />} />

                        {/* HISTORY TRAINER ROUTES */}
                        <Route path="/history-trainer/:trainerId" element={<HistoryTrainerPage />} />
                        <Route path="/history-trainer/:trainerId/:programId/intensity-selection" element={<HistoryTrainerPage />} />

                        {/* NATIVE AMERICAN TRAINER ROUTES */}
                        <Route path="/native-american-trainer/:trainerId" element={<NativeAmericanTrainerPage />} />
                        <Route path="/native-american-trainer/:trainerId/:programId/intensity-selection" element={<NativeAmericanTrainerPage />} />

                        {/* REP COUNT ROUTES FOR DIFFERENT WORKOUT TYPES */}
                        <Route path="/bodyweight-rep-count/:workoutCode" element={<RepCountScreen />} />
                        <Route path="/bands-rep-count/:workoutCode" element={<RepCountScreen />} />
                        <Route path="/strength-rep-count/:workoutCode" element={<RepCountScreen />} />
                        <Route path="/advanced-bodyweight-rep-count/:workoutCode" element={<RepCountScreen />} />
                        <Route path="/everything-rep-count/:workoutCode" element={<RepCountScreen />} />
                        <Route path="/battle-rep-count/:workoutCode" element={<BattleRepCount />} />
                        <Route path="/hero-rep-count/:workoutCode" element={<RepCountScreen />} />
                        <Route path="/japan-rep-count/:workoutCode" element={<RepCountScreen />} />
                        <Route path="/knights-rep-count/:workoutCode" element={<RepCountScreen />} />
                        <Route path="/history-rep-count/:workoutCode" element={<RepCountScreen />} />
                        <Route path="/native-american-rep-count/:workoutCode" element={<RepCountScreen />} />

                        {/* WORKOUT ROUTES WITH PARAMETERS */}
                        <Route path="/bodyweight-workout/:workoutCode/:repCount/:setCount" element={<Workout />} />
                        <Route path="/bands-workout/:workoutCode/:repCount/:setCount" element={<Workout />} />
                        <Route path="/strength-workout/:workoutCode/:repCount/:setCount" element={<Workout />} />
                        <Route path="/advanced-bodyweight-workout/:workoutCode/:repCount/:setCount" element={<Workout />} />
                        <Route path="/everything-workout/:workoutCode/:repCount/:setCount" element={<Workout />} />
                        <Route path="/hero-workout/:workoutCode/:repCount/:setCount" element={<Workout />} />
                        <Route path="/japan-workout/:workoutCode/:repCount/:setCount" element={<Workout />} />
                        <Route path="/knights-workout/:workoutCode/:repCount/:setCount" element={<Workout />} />
                        <Route path="/history-workout/:workoutCode/:repCount/:setCount" element={<Workout />} />
                        <Route path="/native-american-workout/:workoutCode/:repCount/:setCount" element={<Workout />} />

                        {/* TRAINER WORKOUT ROUTES */}
                        <Route path="/advanced-trainer-workout/:trainerId/:programId/:repCount/:setCount" element={<Workout />} />
                        <Route path="/strength-trainer-workout/:trainerId/:programId/:repCount/:setCount" element={<Workout />} />
                        <Route path="/bands-trainer-workout/:trainerId/:programId/:repCount/:setCount" element={<Workout />} />
                        <Route path="/everything-trainer-workout/:trainerId/:programId/:repCount/:setCount" element={<Workout />} />
                        <Route path="/hero-trainer-workout/:trainerId/:programId/:repCount/:setCount" element={<Workout />} />
                        <Route path="/japan-trainer-workout/:trainerId/:programId/:repCount/:setCount" element={<Workout />} />
                        <Route path="/knight-trainer-workout/:trainerId/:programId/:repCount/:setCount" element={<Workout />} />
                        <Route path="/history-trainer-workout/:trainerId/:programId/:repCount/:setCount" element={<Workout />} />
                        <Route path="/native-american-trainer-workout/:trainerId/:programId/:repCount/:setCount" element={<Workout />} />

                        {/* BATTLE MODE SPECIFIC ROUTES */}
                        <Route path="/battle-workout/:battleCode/:repCount/:setCount" element={<BattleWorkout />} />
                        <Route path="/battle-complete/:battleCode/:finalScore/:totalReps/:sets/:completedSets" element={<BattleComplete />} />
                    </Routes>
                </div>
            </Router>
        </WorkoutProvider>
    );
}

export default App;